import type { ApiResponse, ApiErrorResponse } from '@/types';
import { addToSyncQueue } from '@/lib/offline-store';
import {
  clearTabAccessToken,
  decodeUserId,
  getAccessToken,
  getActiveTenantId,
  getTabTenantId,
  setTabAccessToken,
} from '@/lib/active-tenant';
import { getDesktopApiBaseUrl } from '@/lib/desktop';
import { ApiError, OfflineNotQueuedError, OfflineQueuedError } from '@efacture/web-billing/billing-host/errors';
import { billingMutationRule, type BillingMutationMethod } from '@efacture/web-billing/lib/billing-mutations';

export { ApiError, OfflineNotQueuedError, OfflineQueuedError };

/**
 * Get the base URL for API requests.
 *
 * Desktop (Electron): the embedded API URL is resolved via IPC before
 * the app renders (initDesktopApi in main.tsx) — read synchronously here.
 * Web: VITE_API_URL override, else '/api' (same-origin/proxy).
 */
export function getBaseUrl(): string {
  return getDesktopApiBaseUrl() || import.meta.env.VITE_API_URL || '/api';
}

export { getAccessToken };

/**
 * Stores this tab's access token (see `@/lib/active-tenant`); `expiresAt`
 * is kept when not given.
 */
export function setAccessToken(token: string, expiresAt?: string): void {
  setTabAccessToken(token, expiresAt);
}

/** Drops this tab's access token. */
export function clearAccessToken(): void {
  clearTabAccessToken();
}

/**
 * Request options type
 */
interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined> | undefined;
  /** Headers the caller adds to the request. An entry queued offline keeps
   * them, and its replay sends them again. */
  headers?: Readonly<Record<string, string>>;
  /** Skip auto-queueing on a network failure — set by the sync-queue replay
   * path (`apiClient.replay`), which owns its own retry/hold/fail decision
   * for the entry already in the queue, and by a `post` whose effect is
   * meaningless when replayed later (`post(…, { noQueue: true })`). */
  noQueue?: boolean;
  /** A `TypeError` thrown by `fetch()` itself becomes an
   * `OfflineNotQueuedError`. Set only by the billing writes that are never
   * queued (`sendBillingMutation`); a failure while reading the response
   * keeps its own error. */
  failWhenOffline?: boolean;
  /** Overrides the auto-generated `Idempotency-Key` — set only by the
   * sync-queue replay path, to resend one queued entry under the SAME key
   * on every attempt. */
  idempotencyKey?: string;
  /** Dossier the request is meant for, in place of this tab's — set only
   * by the sync-queue replay path, with the dossier the entry was queued
   * under. */
  tenantId?: string;
}

// ─────────────────────────────────────────────────────────────
// Dossier of the request (X-Tenant-ID)
// ─────────────────────────────────────────────────────────────

/**
 * Session routes act on the user, not on a dossier: switching dossier,
 * listing the accessible ones, logging out, the profile. They carry no
 * X-Tenant-ID and are never held back, so a tab whose token left its
 * dossier can still switch back or log out.
 */
function isTenantScoped(path: string): boolean {
  return !path.startsWith('/auth/');
}

export const TENANT_MISMATCH_CODE = 'TENANT_MISMATCH';

const TENANT_MISMATCH_MESSAGE =
  "La session de cet onglet n'est plus sur ce dossier. Choisissez le dossier avant de continuer.";

/**
 * `X-Tenant-ID` for a request this tab sends outside `apiClient`. Empty
 * until the tab has established its session.
 */
export function tenantHeaders(): Record<string, string> {
  const tenantId = getTabTenantId();
  return tenantId ? { 'X-Tenant-ID': tenantId } : {};
}

/**
 * `Authorization` and `X-Tenant-ID` of this tab, for a request sent
 * outside `apiClient` (assistant query, file downloads, event stream).
 */
export function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...tenantHeaders() };
}

/**
 * Refuses, before anything is sent, a request whose dossier is no longer
 * the token's: the token would scope it to the other dossier. The API
 * refuses the same request (409 TENANT_MISMATCH) when one gets through;
 * this check keeps it, and the form it came from, in this tab.
 */
function assertTokenOnDossier(expectedTenantId: string | null): void {
  if (expectedTenantId === null) return;
  const activeTenantId = getActiveTenantId();
  if (activeTenantId === null || activeTenantId === expectedTenantId) return;
  throw new ApiError(TENANT_MISMATCH_MESSAGE, TENANT_MISMATCH_CODE, 409, { activeTenantId });
}

// ─────────────────────────────────────────────────────────────
// Erreurs d'abonnement (402/403) — interception globale SANS crash :
// un CustomEvent est émis, la bannière d'upsell (UpsellBanner, montée
// dans AppLayout) l'affiche en français avec le lien abonnement.
// L'ApiError reste levée pour que l'appelant gère son propre état.
// ─────────────────────────────────────────────────────────────

/** Nom de l'événement window émis pour les erreurs d'abonnement. */
export const BILLING_ERROR_EVENT = 'efacture:billing-error';

export interface BillingErrorEventDetail {
  /** PLAN_INSUFFISANT (403) ou ABONNEMENT_SUSPENDU (402). */
  code: string;
  /** Message français du serveur. */
  message: string;
  requiredPlan?: string;
  currentPlan?: string;
  feature?: string;
}

const BILLING_ERROR_CODES = new Set(['PLAN_INSUFFISANT', 'ABONNEMENT_SUSPENDU']);

function dispatchBillingError(errorData: ApiErrorResponse): void {
  const code = errorData.error?.code;
  if (!code || !BILLING_ERROR_CODES.has(code) || typeof window === 'undefined') {
    return;
  }
  // details du plan-gating : { feature, currentPlan, requiredPlan }
  const details = (errorData.error?.details ?? {}) as Record<string, unknown>;
  const detail: BillingErrorEventDetail = {
    code,
    message: errorData.error?.message || 'Fonctionnalité non incluse dans votre offre',
    ...(typeof details['requiredPlan'] === 'string'
      ? { requiredPlan: details['requiredPlan'] as string }
      : {}),
    ...(typeof details['currentPlan'] === 'string'
      ? { currentPlan: details['currentPlan'] as string }
      : {}),
    ...(typeof details['feature'] === 'string'
      ? { feature: details['feature'] as string }
      : {}),
  };
  window.dispatchEvent(new CustomEvent<BillingErrorEventDetail>(BILLING_ERROR_EVENT, { detail }));
}

/**
 * Build URL with query parameters
 */
function buildUrl(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const baseUrl = getBaseUrl();

  // Ensure baseUrl ends without slash and path starts with slash for clean concatenation
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Construct full URL - concatenate base + path, then use URL for param handling
  const fullPath = `${normalizedBase}${normalizedPath}`;
  const url = new URL(fullPath, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Parse API response
 */
async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');

  if (!contentType || !contentType.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError(
        'Une erreur inattendue est survenue',
        'UNKNOWN_ERROR',
        response.status
      );
    }
    return {} as T;
  }

  const data = await response.json();

  if (!response.ok) {
    const errorData = data as ApiErrorResponse;
    // 402 ABONNEMENT_SUSPENDU / 403 PLAN_INSUFFISANT → bannière upsell
    // globale (aucun crash — l'erreur reste levée pour l'appelant).
    dispatchBillingError(errorData);
    throw new ApiError(
      errorData.error?.message || 'Une erreur est survenue',
      errorData.error?.code || 'UNKNOWN_ERROR',
      response.status,
      errorData.error?.details
    );
  }

  return (data as ApiResponse<T>).data;
}

/**
 * Session-lifecycle endpoints: their 401 belongs to `AuthContext`, not to
 * the blanket handler below.
 *
 * `GET /auth/me` answers 401 whenever the access token has expired — the
 * ordinary case on startup, and precisely what the refresh token exists to
 * repair. Clearing the token and navigating to /login here destroyed that
 * recovery before the caller ever saw the error, so an expired access token
 * meant a login screen even when the session was perfectly renewable.
 * `POST /auth/refresh` likewise: a 401 there is the one signal that ends the
 * session, and `AuthContext.endSession` owns that decision (it also purges
 * the query cache, which this handler does not).
 *
 * Every other route keeps the redirect: a 401 outside the auth flow means
 * the session really is gone.
 */
const SESSION_LIFECYCLE_PATHS = ['/auth/me', '/auth/refresh'];

/**
 * Matched against the RAW path, before `buildUrl` appends query parameters
 * (those travel in the `params` option, never in `path`). The `?` form is
 * kept only so a caller that inlines a query string in the path still
 * matches.
 */
function ownsItsUnauthorized(path: string): boolean {
  return SESSION_LIFECYCLE_PATHS.some(
    (p) => path === p || path.startsWith(`${p}?`)
  );
}

/**
 * 401 codes that answer a credential the user just typed (password, 2FA
 * code). The session is intact: the caller shows the server's message.
 */
const CREDENTIAL_REJECTION_CODES = new Set([
  'INVALID_CREDENTIALS',
  'INVALID_TOTP_CODE',
  'INVALID_PASSWORD',
]);

/** The error code of a JSON error answer, read from a clone; '' when absent. */
async function errorCodeOf(response: Response): Promise<string> {
  try {
    const body = (await response.clone().json()) as ApiErrorResponse;
    return body.error?.code ?? '';
  } catch {
    return '';
  }
}

async function isCredentialRejection(response: Response): Promise<boolean> {
  return CREDENTIAL_REJECTION_CODES.has(await errorCodeOf(response));
}

/**
 * Renews the session after a request answered 401 TOKEN_EXPIRED.
 * `AuthProvider` registers its single-flight refresh, which sends this tab's
 * expired access token so an open client file resumes, and ends the session
 * itself when the refresh answers 401/403. Null outside `AuthProvider`.
 */
type SessionRefresher = () => Promise<void>;
let sessionRefresher: SessionRefresher | null = null;
let renewalInFlight: Promise<void> | null = null;

export function setSessionRefresher(refresher: SessionRefresher | null): void {
  sessionRefresher = refresher;
}

export const SESSION_CHANGED_CODE = 'SESSION_CHANGED';

/**
 * Thrown instead of resending a request whose user is no longer this tab's:
 * the request is not sent under the other user, and that user's session
 * is left as it is.
 */
function sessionChanged(): ApiError {
  return new ApiError(
    "Session changée : cette demande a été faite par un autre utilisateur et n'a pas été renvoyée.",
    SESSION_CHANGED_CODE,
    401
  );
}

/**
 * After a 401 on a request sent with `sentToken`: true when the request may
 * be sent once more on a renewed token of the SAME user. Only TOKEN_EXPIRED
 * is renewed. A token replaced since the request left needs no renewal, and
 * concurrent callers share one. False when the refresh ends the session
 * (401/403). Throws `sessionChanged()` when the tab's token now belongs to
 * another user; any other refresh failure is thrown and the token stays.
 */
async function renewAfterExpiry(response: Response, sentToken: string | null): Promise<boolean> {
  const refresher = sessionRefresher;
  if (refresher === null || sentToken === null) return false;
  if ((await errorCodeOf(response)) !== 'TOKEN_EXPIRED') return false;
  const sentUserId = decodeUserId(sentToken);
  const sameUser = (token: string) => sentUserId !== null && decodeUserId(token) === sentUserId;
  const current = getAccessToken();
  if (current === null) return false;
  if (current !== sentToken) {
    if (sameUser(current)) return true;
    throw sessionChanged();
  }
  if (renewalInFlight === null) {
    const renewal = refresher();
    renewalInFlight = renewal;
    const release = () => {
      if (renewalInFlight === renewal) renewalInFlight = null;
    };
    renewal.then(release, release);
  }
  try {
    await renewalInFlight;
  } catch (error) {
    if (error instanceof ApiError && (error.isAuthError() || error.isForbidden())) return false;
    throw error;
  }
  // A refresh answered for another account ends this tab's session without
  // touching that login (`AuthProvider`), which leaves no token here.
  const renewed = getAccessToken();
  if (renewed === null || !sameUser(renewed)) throw sessionChanged();
  return true;
}

// Paths that should never be queued offline (auth, reads disguised as POST)
const OFFLINE_QUEUE_EXCLUDE = ['/auth/', '/nlp/', '/public/', '/onboarding/'];

function isQueuableMutation(method: string | undefined, path: string): boolean {
  if (!method || method === 'GET') return false;
  return !OFFLINE_QUEUE_EXCLUDE.some((prefix) => path.startsWith(prefix));
}

// Shell writes replayed later without harm: customer and article records,
// and the count entries of a physical inventory (a late entry is flagged by
// the API from its capture time, and a session no longer counting refuses it).
// Every other shell write is refused offline (`OfflineNotQueuedError`), as
// are the billing rows marked `never` in BILLING_MUTATIONS.
const SHELL_OFFLINE_REPLAY: readonly RegExp[] = [
  /^POST \/clients$/,
  /^(PUT|DELETE) \/clients\/[^/]+$/,
  /^POST \/articles$/,
  /^(PUT|DELETE) \/articles\/[^/]+$/,
  /^POST \/articles\/[^/]+\/toggle-favorite$/,
  /^PATCH \/notifications\/(read-all|[^/]+\/read)$/,
  /^POST \/stock\/inventories\/[^/]+\/counts$/,
];

/** True when a failed write of this method and path may be queued for replay. */
function isOfflineReplayable(method: string | undefined, path: string): boolean {
  if (!method) return false;
  const bare = path.split('?')[0] ?? path;
  const row = billingMutationRule(method as BillingMutationMethod, bare);
  if (row) return row.queue === 'offline-replay';
  return SHELL_OFFLINE_REPLAY.some((pattern) => pattern.test(`${method} ${bare}`));
}

/**
 * Session-end navigation. On Electron the app is loaded from `file://`
 * (router.tsx switches to hash routing for exactly this reason) — a
 * root-relative `/login` resolves against `file:///…/index.html` to
 * `file:///login`, which does not exist, and leaves the window on a
 * load-failure page with no way back. Route through the hash there.
 */
export function redirectToLogin(): void {
  if (window.location.protocol === 'file:') {
    window.location.hash = '#/login';
  } else {
    window.location.href = '/login';
  }
}

/**
 * Main fetch wrapper with authentication and error handling.
 * When offline and the request is a mutation (POST/PUT/PATCH/DELETE),
 * queues it to IndexedDB for later sync instead of failing.
 * An expired access token is renewed once (`renewAfterExpiry`) and the
 * request sent again under the same Idempotency-Key; `afterRenewal` marks
 * that second attempt.
 */
async function fetchApi<T>(
  path: string,
  options: RequestOptions = {},
  afterRenewal = false
): Promise<T> {
  const {
    body,
    params,
    headers: customHeaders,
    noQueue,
    failWhenOffline,
    idempotencyKey: idempotencyKeyOverride,
    tenantId: replayTenantId,
    ...restOptions
  } = options;

  // The dossier this request is for: a replayed entry's own, else this tab's.
  const isReplay = replayTenantId !== undefined;
  const expectedTenantId = isTenantScoped(path)
    ? isReplay
      ? replayTenantId
      : getTabTenantId()
    : null;
  assertTokenOnDossier(expectedTenantId);

  const url = buildUrl(path, params);
  const token = getAccessToken();
  // A file upload is sent as is, with the multipart Content-Type the browser
  // sets, and never queued: FormData cannot be stored in IndexedDB.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const queuable = !isForm && isQueuableMutation(restOptions.method, path);
  const replayable = queuable && isOfflineReplayable(restOptions.method, path);
  // One key for every attempt of one mutation (first try and each offline
  // replay), so the API dedupes a request whose response was lost. A first
  // attempt mints it; `apiClient.replay` passes the queued entry's id.
  const idempotencyKey =
    idempotencyKeyOverride ?? (queuable && !noQueue ? crypto.randomUUID() : undefined);

  // Never send Content-Type: application/json without a body — Fastify
  // rejects (400 "Body cannot be empty") a bodyless POST typed that way
  // (e.g. /accounting/entries/:id/post, /accounting/activate, lock/unlock).
  const headers: HeadersInit = {
    ...(body !== undefined && body !== null && !isForm ? { 'Content-Type': 'application/json' } : {}),
    'Accept': 'application/json',
    ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    ...(expectedTenantId ? { 'X-Tenant-ID': expectedTenantId } : {}),
    ...customHeaders,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  // Serialized OUTSIDE the network try/catch below: a body that cannot be
  // JSON-serialized (a circular reference, a BigInt) must fail as itself,
  // not be mistaken for a dropped connection and silently queued offline.
  const requestBody: BodyInit | undefined = isForm
    ? (body as FormData)
    : body !== undefined && body !== null
      ? JSON.stringify(body)
      : undefined;

  let response: Response;
  try {
    response = await fetch(url, {
      ...restOptions,
      headers,
      ...(requestBody !== undefined ? { body: requestBody } : {}),
    });
  } catch (error) {
    // A `TypeError` here comes from `fetch()` itself (offline, DNS, CORS,
    // connection reset); each browser words its message differently, so
    // the type is matched, not the text. `noQueue` (sync-queue replay)
    // never queues: that caller already owns the entry.
    if (!noQueue && error instanceof TypeError && replayable) {
      // Stamped with the dossier checked above and the user of the token
      // sent, not re-read: a refresh may have moved the dossier, or another
      // user logged in, while the request failed.
      await addToSyncQueue(
        {
          id: idempotencyKey as string,
          method: restOptions.method as 'POST' | 'PUT' | 'PATCH' | 'DELETE',
          path,
          body,
          ...(customHeaders !== undefined && { headers: customHeaders }),
        },
        expectedTenantId ?? getActiveTenantId(),
        token ? decodeUserId(token) : null
      );
      throw new OfflineQueuedError();
    }
    if ((failWhenOffline || (queuable && !noQueue)) && error instanceof TypeError) {
      throw new OfflineNotQueuedError();
    }
    throw error;
  }

  // Handle token expiration — except on the session-lifecycle routes,
  // whose 401 is AuthContext's to interpret (see above), and on a rejected
  // credential, which parseResponse turns into the server's own message.
  if (
    response.status === 401 &&
    !ownsItsUnauthorized(path) &&
    !(await isCredentialRejection(response))
  ) {
    if (!afterRenewal && (await renewAfterExpiry(response, token))) {
      return fetchApi<T>(
        path,
        { ...options, ...(idempotencyKey !== undefined ? { idempotencyKey } : {}) },
        true
      );
    }
    clearAccessToken();
    redirectToLogin();
    throw new ApiError('Session expirée', 'UNAUTHORIZED', 401);
  }

  return parseResponse<T>(response);
}

/**
 * Fetches raw bytes with the same auth and error handling as a download.
 * Used both by the download tail below and by in-app viewers, which read the
 * bytes without ever writing a file to disk.
 */
async function requestBlob(
  path: string,
  signal?: AbortSignal,
  post?: { body: unknown },
  afterRenewal = false
): Promise<Blob> {
  const expectedTenantId = getTabTenantId();
  assertTokenOnDossier(expectedTenantId);

  const url = buildUrl(path);
  const token = getAccessToken();

  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (expectedTenantId) {
    headers['X-Tenant-ID'] = expectedTenantId;
  }
  if (post) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method: post ? 'POST' : 'GET',
    headers,
    ...(post ? { body: JSON.stringify(post.body) } : {}),
    ...(signal ? { signal } : {}),
  });

  if (response.status === 401) {
    if (!afterRenewal && (await renewAfterExpiry(response, token))) {
      return requestBlob(path, signal, post, true);
    }
    clearAccessToken();
    redirectToLogin();
    throw new ApiError('Session expirée', 'UNAUTHORIZED', 401);
  }

  if (!response.ok) {
    // Try to parse error response
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const errorData = (await response.json()) as ApiErrorResponse;
      dispatchBillingError(errorData);
      throw new ApiError(
        errorData.error?.message || 'Une erreur est survenue',
        errorData.error?.code || 'DOWNLOAD_ERROR',
        response.status
      );
    }
    throw new ApiError(
      'Erreur lors du téléchargement',
      'DOWNLOAD_ERROR',
      response.status
    );
  }

  return response.blob();
}

/**
 * Download a blob from the API (for PDFs, etc.)
 */
async function downloadBlob(
  path: string,
  filename: string,
  post?: { body: unknown }
): Promise<void> {
  const blob = await requestBlob(path, undefined, post);

  // Create download link
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

/** Offline options of a PUT, PATCH or DELETE. */
interface WriteOptions {
  noQueue?: boolean;
  failWhenOffline?: boolean;
}

function writeOptions(options: WriteOptions | undefined): Pick<RequestOptions, 'noQueue' | 'failWhenOffline'> {
  return {
    ...(options?.noQueue === true && { noQueue: true }),
    ...(options?.failWhenOffline === true && { failWhenOffline: true }),
  };
}

/**
 * API client with convenience methods
 */
export const apiClient = {
  /**
   * GET request
   */
  get<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    return fetchApi<T>(path, { method: 'GET', params });
  },

  /**
   * POST request. `noQueue` makes a network failure throw instead of
   * queuing the request for offline replay. `idempotencyKey` is sent as the
   * Idempotency-Key header: a retry under the same key answers the first
   * success instead of repeating the write. `failWhenOffline` turns a
   * `fetch()` network failure into an `OfflineNotQueuedError`. `headers`
   * are added to the request, and kept by an offline-queued entry.
   */
  post<T>(
    path: string,
    body?: unknown,
    options?: {
      noQueue?: boolean;
      idempotencyKey?: string;
      failWhenOffline?: boolean;
      headers?: Readonly<Record<string, string>>;
    }
  ): Promise<T> {
    return fetchApi<T>(path, {
      method: 'POST',
      body,
      noQueue: options?.noQueue === true,
      ...(options?.failWhenOffline === true && { failWhenOffline: true }),
      ...(options?.idempotencyKey !== undefined && { idempotencyKey: options.idempotencyKey }),
      ...(options?.headers !== undefined && { headers: options.headers }),
    });
  },

  /**
   * PUT request. `noQueue` and `failWhenOffline` act as on `post`.
   */
  put<T>(path: string, body?: unknown, options?: WriteOptions): Promise<T> {
    return fetchApi<T>(path, { method: 'PUT', body, ...writeOptions(options) });
  },

  /**
   * PATCH request. `noQueue` and `failWhenOffline` act as on `post`.
   */
  patch<T>(path: string, body?: unknown, options?: WriteOptions): Promise<T> {
    return fetchApi<T>(path, { method: 'PATCH', body, ...writeOptions(options) });
  },

  /**
   * PUT of a multipart form (file upload). Fails when offline rather than
   * queuing.
   */
  putForm<T>(path: string, form: FormData): Promise<T> {
    return fetchApi<T>(path, { method: 'PUT', body: form });
  },

  /**
   * DELETE request
   */
  delete<T>(path: string, options?: WriteOptions): Promise<T> {
    return fetchApi<T>(path, { method: 'DELETE', ...writeOptions(options) });
  },

  /**
   * Replays one offline-queued mutation (sync-queue.ts). Unlike the
   * ordinary methods above, a network failure here is never re-queued —
   * the caller already owns the entry and decides retry/hold/fail itself;
   * queuing again would duplicate it. `idempotencyKey` is the entry's own
   * id, resent unchanged on every attempt so a response lost to a dropped
   * connection cannot be duplicated by the next one either. `tenantId` is
   * the dossier the entry was queued under: the request is refused
   * (TENANT_MISMATCH) unless the token is on that dossier. `headers` are
   * the ones the entry was queued with.
   */
  replay<T>(
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body: unknown,
    idempotencyKey: string,
    tenantId: string,
    headers?: Readonly<Record<string, string>>
  ): Promise<T> {
    return fetchApi<T>(path, {
      method,
      body,
      noQueue: true,
      idempotencyKey,
      tenantId,
      ...(headers !== undefined && { headers }),
    });
  },

  /**
   * Download a file as blob
   */
  download(path: string, filename: string): Promise<void> {
    return downloadBlob(path, filename);
  },

  /**
   * Download the file a POST returns (a document built from the request
   * body). Fails when offline rather than queuing.
   */
  downloadPost(path: string, body: unknown, filename: string): Promise<void> {
    return downloadBlob(path, filename, { body });
  },

  /**
   * Raw bytes of a file, for an in-app viewer. Same auth and refusals as
   * `download`, without touching the filesystem.
   */
  blob(path: string, signal?: AbortSignal): Promise<Blob> {
    return requestBlob(path, signal);
  },
};

export default apiClient;
